<template>
  <div>
    <van-tabs v-model="filterType" background="#fff" color="#FF6B00" line-width="30" line-height="3">
      <van-tab title="全部" name="all" />
      <van-tab title="获取" name="income" />
      <van-tab title="支出" name="expense" />
    </van-tabs>
    <van-collapse v-model="activeNames">
      <van-collapse-item 
        v-for="(group, month) in groupedData" 
        :key="month" 
        :name="month"
      >
        <template #title>
          <div class="collapse-title">
            <span class="month-year">{{ month }}</span>
            <span class="monthly-summary">
              <template v-if="filterType === 'all'">
                获得:<span class='income-amount'>{{ group.totalEarned }}</span> 支出:<span class='expense-amount'>{{ group.totalSpent }}</span>
              </template>
              <template v-else-if="filterType === 'income'">
                获得:<span class='income-amount'>{{ group.totalEarned }}</span>
              </template>
              <template v-else-if="filterType === 'expense'">
                支出:<span class='expense-amount'>{{ group.totalSpent }}</span>
              </template>
            </span>
          </div>
        </template>
        <van-cell
          v-for="item in group.items"
          :key="item.id"
          :title="item.description"
          :label="item.timestamp"
          title-style="flex:4"
        >
          <template  >
            <span :class="['item-points', item.points > 0 ? 'positive' : 'negative']">
              {{ item.points }}
            </span>
          </template>
        </van-cell>
      </van-collapse-item>
    </van-collapse>
  </div>
</template>

<script>
import { Collapse, CollapseItem, Tabs, Tab } from 'vant';
import dayjs from 'dayjs'; // Using dayjs for date formatting

export default {
  name: 'MonthlyCollapseList',
  components: {
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
  },
  props: {
    // Expecting data in the format: [{ id: 1, description: '...', timestamp: '...', points: 100 }, ...]
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      activeNames: [], // Controls which collapse items are open
      filterType: 'all', // 新增筛选类型
    };
  },
  computed: {
    groupedData() {
      const groups = {};
      this.data.forEach(item => {
        const date = dayjs(item.timestamp);
        const monthYear = date.format('YYYY-MM'); // Group by YYYY-MM

        if (!groups[monthYear]) {
          // Initialize with earned and spent totals
          groups[monthYear] = { items: [], totalEarned: 0, totalSpent: 0 }; 
        }
        // 根据筛选类型过滤
        if (
          this.filterType === 'all' ||
          (this.filterType === 'income' && item.points > 0) ||
          (this.filterType === 'expense' && item.points < 0)
        ) {
          groups[monthYear].items.push(item);
        }
        // Accumulate earned and spent points separately
        if (item.points > 0) {
          groups[monthYear].totalEarned += item.points;
        } else {
          // Spent points are negative, add their absolute value to totalSpent
          groups[monthYear].totalSpent += Math.abs(item.points);
        }
      });

      // 移除没有items的月份
      Object.keys(groups).forEach(key => {
        if (!groups[key].items.length) {
          delete groups[key];
        }
      });

      // Sort groups by monthYear descending (newest first)
      const sortedKeys = Object.keys(groups).sort().reverse();
      const sortedGroups = {};
      sortedKeys.forEach(key => {
        sortedGroups[key] = groups[key];
        // Sort items within each group by timestamp descending
        sortedGroups[key].items.sort((a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf());
      });
      return sortedGroups;
    },
  },
  watch: {
    // Watch for changes in groupedData to update activeNames
    groupedData: {
      handler(newGroups) {
        const sortedKeys = Object.keys(newGroups);
        // Set the first group to be open by default only if activeNames is empty
        // and there are groups
        if (sortedKeys.length > 0 && this.activeNames.length === 0) {
          this.activeNames = [sortedKeys[0]];
        }
      },
      immediate: true, // Run the handler immediately after component creation
    },
    filterType() {
      // 切换筛选类型时，重置展开项为第一个有数据的月份
      this.activeNames = [];
      this.$nextTick(() => {
        const keys = Object.keys(this.groupedData);
        if (keys.length > 0) {
          this.activeNames = [keys[0]];
        }
      });
    },
  },
  methods: {
    formatMonthYear(monthYear) {
      // Format 'YYYY-MM' to 'MM月 YYYY'
      return dayjs(monthYear).format('MM月 YYYY');
    },
    formatTimestamp(timestamp) {
      // Format timestamp to 'YYYY-MM-DD HH:mm:ss'
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    },
  },
};
</script>

<style scoped lang="less">
.van-collapse-item {
  margin-bottom: 10px; /* Add space between month blocks */
  border-radius: 8px;
  overflow: hidden; /* Ensure border-radius applies correctly */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  /deep/ .van-cell {
    padding-top: 12px;
    padding-bottom: 12px;
    align-items: center;
    background-color: #fff; /* Ensure background is white */
    border-bottom: 1px solid #f7f7f7; /* Subtle separator */

    &::after {
      display: none; /* Hide default Vant border */
    }
  }

  /deep/ .van-collapse-item__content {
    padding: 0 16px 10px 16px; /* Adjust padding for content */
    background-color: #fff; /* Ensure background is white */
    color: #666;
    font-size: 13px;
  }
}

.collapse-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 15px;
  color: #333;

  .month-year {
    font-weight: bold;
    position: relative;
    padding-left: 12px;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 16px;
      background-color: #FF6B00;
      border-radius: 2px;
    }
  }
  .monthly-summary {
    color: #666;
    font-size: 14px;
    display: flex;
    gap: 8px;
    align-items: center;
  }
}

.item-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0; /* Add padding top/bottom */
  border-bottom: 1px solid #f0f0f0; /* Separator for items */

  &:last-child {
    border-bottom: none; /* Remove border for the last item */
  }
}

.item-info {
  display: flex;
  flex-direction: column;
}

.item-description {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.item-timestamp {
  font-size: 12px;
  color: #999;
}

.item-points {
  font-size: 16px;
  font-weight: bold;

  &.positive {
    color: #FF6B00; /* Color for positive points */
  }

  &.negative {
    color: #32CD32; /* Change color for negative points (e.g., green for spending) */
  }
}

.income-amount {
  color: #FF5722; /* Orange for income */
  margin: 0 2px; /* Add slight spacing */
}

.expense-amount {
  color: #4CAF50; /* Green for expense */
  margin: 0 2px; /* Add slight spacing */
}
</style>